import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CardHeader,
  IconButton,
  CardContent,
  Divider,
  Typography,
  Container,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@material-ui/core';
import BarChart from '../BarChart';
import LineChart from '../LineChart';
import HeatMap from '../../assets/heatmap.svg';
import BarChartVerticalIcon from '../../assets/barChartVertical.svg';
import BarChartHorizontalIcon from '../../assets/barChartHorizontal.svg';
import LineChartIcon from '../../assets/lineChart.svg';
import TableIcon from '../../assets/table.svg';
import TableChart from '../TableChart';
import HeatMapChart from '../HeatMapChart';
import { optionStyles } from './styles';
import {
  chartDataObject,
  chartDataLegendObject,
  toddowChartDataObject,
  tableChartDataObject,
  multilineData,
} from '../../@types/chartData';
import OnModal from '../Modal';

import api from '../../services/api';

interface TableData {
  rows: tableChartDataObject[];
  legends: string[];
}
interface Charts {
  chartType: string;
  heat?: toddowChartDataObject[];
  chart?: chartDataObject[];
  chartLegend?: chartDataLegendObject;
  table?: TableData;
  multi?: multilineData[];
  title: string;
  legendY?: string;
  legendOne?: string;
  legendTwo?: string;
  percent?: boolean;
  request: string;
  legend?: boolean;
  setChart:
  | React.Dispatch<React.SetStateAction<toddowChartDataObject[]>>
  | React.Dispatch<React.SetStateAction<chartDataObject[]>>
  | React.Dispatch<React.SetStateAction<tableChartDataObject[]>>
  | React.Dispatch<React.SetStateAction<multilineData[]>>
  | React.Dispatch<React.SetStateAction<chartDataLegendObject[]>>;
  loading: boolean;
  setlegend: React.Dispatch<React.SetStateAction<string>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Params {
  occurred_min: string;
  occurred_max: string;
}

interface Props {
  charts: Charts[];
  params: Params;
}

const ChartsGenerator: React.FC<Props> = ({ charts, params }) => {
  const classes = optionStyles();
  const [displayedChart, setDisplayedChart] = useState<number>(1);

  const [yearMultiline, setYearMultiline] = useState<string>('');

  const handleChange = (event: any) => {
    setYearMultiline(event.target.value);
  };

  const authHeader = useMemo(() => {
    return { Authorization: `Token ${localStorage.getItem('auth')}` };
  }, []);

  const selectIcon = (type: string): string => {
    if (type === 'heat') {
      return HeatMap;
    }
    if (type === 'line') {
      return LineChartIcon;
    }
    if (type === 'barv') {
      return BarChartVerticalIcon;
    }
    if (type === 'barh') {
      return BarChartHorizontalIcon;
    }
    if (type === 'table') {
      return TableIcon;
    }
    if (type === 'chartLegend') {
      return BarChartVerticalIcon;
    }
    return LineChartIcon;
  };

  // geração de gráficos a partir das opções escolhidas
  const selectChart = (chart: Charts): JSX.Element => {
    if (chart.chartType === 'heat') {
      return (
        <CardContent className={classes.heightContent}>
          {chart.heat ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {chart.title}
              </Typography>
              <OnModal>
                <HeatMapChart dataHeatMap={chart.heat} />
              </OnModal>
            </>
          ) : null}
        </CardContent>
      );
    }

    if (chart.chartType === 'line') {
      return (
        <CardContent>
          {chart.chart ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {chart.title}
              </Typography>
              <OnModal>
                <LineChart
                  legendY={chart.legendY ? chart.legendY : ''}
                  legendOne={chart.legendOne ? chart.legendOne : ''}
                  legendTwo={chart.legendTwo ? chart.legendTwo : ''}
                  data={chart.chart}
                  lineTwo
                />
              </OnModal>
            </>
          ) : null}
        </CardContent>
      );
    }

    if (chart.chartType === 'barv') {
      return (
        <CardContent>
          {chart.chart ? (
            <>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  {chart.title}
                </Typography>
                {chart.legendY ? (
                  <Typography variant="subtitle2" gutterBottom>
                    {chart.legendY}
                  </Typography>
                ) : null}
              </Box>

              <OnModal>
                <BarChart
                  legendY={chart.legendY ? chart.legendY : ''}
                  legendOne={chart.legendOne ? chart.legendOne : ''}
                  legendTwo={chart.legendTwo ? chart.legendTwo : ''}
                  data={chart.chart}
                  percent={chart.percent}
                />
              </OnModal>
            </>
          ) : null}
        </CardContent>
      );
    }
    if (chart.chartType === 'barh') {
      return (
        <CardContent className={classes.heightContentBar}>
          {chart.chart ? (
            <>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  {chart.title}
                </Typography>
                {chart.legend ? (
                  <Typography variant="subtitle2" gutterBottom>
                    {chart.legendY}
                  </Typography>
                ) : null}
              </Box>
              <OnModal>
                <BarChart
                  legendY={chart.legendY ? chart.legendY : ''}
                  legendOne={chart.legendOne ? chart.legendOne : ''}
                  legendTwo={chart.legendTwo ? chart.legendTwo : ''}
                  direction
                  percent={chart.percent}
                  data={chart.chart}
                />
              </OnModal>
            </>
          ) : null}
        </CardContent>
      );
    }
    if (chart.chartType === 'chartLegend') {
      return (
        <CardContent className={classes.heightContentBar}>
          {chart.chartLegend ? (
            <>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  {chart.title}
                </Typography>
                {chart.chartLegend.nInformado && chart.chartLegend.total ? (
                  <Typography variant="subtitle2" gutterBottom>
                    Não informados: {chart.chartLegend.nInformado} de{' '}
                    {chart.chartLegend.total}
                  </Typography>
                ) : null}
              </Box>
              <OnModal>
                <BarChart
                  legendY={chart.legendY ? chart.legendY : ''}
                  legendOne={chart.legendOne ? chart.legendOne : ''}
                  legendTwo={chart.legendTwo ? chart.legendTwo : ''}
                  percent={chart.percent}
                  data={chart.chartLegend.data}
                />
              </OnModal>
            </>
          ) : null}
        </CardContent>
      );
    }
    if (chart.chartType === 'table') {
      return (
        <CardContent>
          {chart.table ? (
            <>
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ margin: '-2% 0 -1% 0' }}
              >
                {chart.title}
              </Typography>
              <OnModal>
                <TableChart data={chart.table} />
              </OnModal>
            </>
          ) : null}
        </CardContent>
      );
    }
    if (chart.chartType === 'multiline') {
      return (
        <CardContent>
          {chart.multi ? (
            <>
              <Container className={classes.containerMulti}>
                <Typography variant="subtitle1" gutterBottom>
                  {chart.title}
                </Typography>
                <div className={classes.formControl}>
                  <Typography variant="subtitle2">Selecione o gráfico</Typography>
                  <Select
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    displayEmpty
                    value={yearMultiline}
                    onChange={handleChange}
                    label="year"
                    className={classes.select}
                    placeholder="Selecione o gráfico"
                  >
                    <MenuItem value="">
                      <em> Nenhum </em>
                    </MenuItem>
                    {chart.multi.map((data) => (
                      <MenuItem value={`${data.value}`} key={data.value}>
                        <em>{data.value}</em>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </Container>
              {chart.multi.map((data) => {
                if (yearMultiline !== '' && data.value === Number(yearMultiline)) {
                  return (
                    <LineChart
                      legendY={chart.legendY ? chart.legendY : ''}
                      legendOne={chart.legendOne ? chart.legendOne : ''}
                      legendTwo={chart.legendTwo ? chart.legendTwo : ''}
                      data={data.results}
                      lineTwo
                    />
                  );
                }
                return '';
              })}
            </>
          ) : null}
        </CardContent>
      );
    }

    return <div />;
  };

  const requestChart = useCallback(
    async (req: string) => {
      charts[displayedChart - 1].setLoading(true);

      api
        .get(`/api/${req}/`, {
          headers: authHeader,
          params,
        })
        .then((data) => {
          if (charts[displayedChart - 1].legend) {
            charts[displayedChart - 1].setChart(data.data.data);
            if (data.data.nInformado) {
              charts[displayedChart - 1].setlegend(
                `Não informados: ${data.data.nInformado} de ${data.data.total}`,
              );
            }
          } else {
            charts[displayedChart - 1].setChart(data.data);
            charts[displayedChart - 1].setlegend('');
          }
          charts[displayedChart - 1].setLoading(false);
        })
        .catch(() => {
          charts[displayedChart - 1].setChart([]);
          charts[displayedChart - 1].setLoading(false);
        });
    },
    [authHeader, params, charts, displayedChart],
  );
  useEffect(() => {
    requestChart(charts[displayedChart - 1].request);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedChart, params]);

  return (
    <Container className={classes.root}>
      <CardHeader
        title="Gráficos"
        className={classes.responsiveCard}
        action={
          <>
            {charts.map((chart, index) => (
              <IconButton
                key={`${chart.title}`}
                className={`${classes.button} ${displayedChart === index + 1 ? classes.buttonActive : ''
                  }`}
                aria-label="Botão"
                onClick={() => {
                  setDisplayedChart(index + 1);
                }}
              >
                <img
                  title={chart.title}
                  className={classes.buttonIncon}
                  src={selectIcon(chart.chartType)}
                  alt="Add icon"
                />
              </IconButton>
            ))}
          </>
        }
      />
      <Divider />
      {charts[displayedChart - 1].loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress className={classes.loadingSpinner} size={60} thickness={5} />
        </div>
      ) : (
        <div className={classes.divResponsive}>
          {selectChart(charts[displayedChart - 1])}
        </div>
      )}
    </Container>
  );
};

export default ChartsGenerator;
